import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, api, unwrap } from '../../api/client';
import { Money } from '../../components/Money';
import { catalogKeys } from './model';
import { ActiveChip } from './ui';

/**
 * Scan or type a barcode/SKU and see the exact item (GET /variants/lookup). Scanners type the code
 * and press Enter, so Enter runs the lookup and selects the text for the next scan.
 */
export function BarcodeLookupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [code, setCode] = useState('');

  const lookup = useQuery({
    queryKey: catalogKeys.lookup(code),
    queryFn: () => unwrap(api.GET('/variants/lookup', { params: { query: { code } } })),
    enabled: open && code !== '',
    retry: false,
    staleTime: 0,
  });
  const notFound = lookup.error instanceof ApiError && lookup.error.status === 404;
  const v = lookup.data;

  const close = () => {
    setText('');
    setCode('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Find by barcode or SKU</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            const next = text.trim();
            if (next === code) void lookup.refetch();
            setCode(next);
            const input = e.currentTarget.querySelector('input');
            input?.select();
          }}
          sx={{ pt: 1 }}
        >
          <TextField
            autoFocus
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Scan now, or type and press Enter"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScannerRounded color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1.125rem', fontFamily: 'ui-monospace, monospace' },
              },
              htmlInput: { maxLength: 64, 'aria-label': 'Barcode or SKU', autoComplete: 'off' },
            }}
          />
        </Box>

        <Box sx={{ minHeight: 168, mt: 2.5 }}>
          {code === '' && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 5 }}>
              Point the scanner at a label. Exact matches only — use search on the list for names.
            </Typography>
          )}
          {lookup.isFetching && !v && (
            <Card variant="outlined" sx={{ p: 2.5 }}>
              <Skeleton width="60%" height={32} />
              <Skeleton width="40%" />
              <Skeleton width="30%" height={36} />
            </Card>
          )}
          {notFound && !lookup.isFetching && (
            <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center', pt: 3 }}>
              <SearchOffRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="subtitle1">Nothing uses “{code}”</Typography>
              <Typography variant="body2" color="text.secondary">
                No active item has this barcode or SKU. Add it to a variant to sell it by scanning.
              </Typography>
            </Stack>
          )}
          {lookup.error && !notFound && <Alert severity="error">{lookup.error.message}</Alert>}
          {v && code !== '' && (
            <Card
              variant="outlined"
              sx={(t) => ({
                p: 2.5,
                borderColor: (t.vars ?? t).palette.primary.main,
                boxShadow: `0 0 0 3px color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} 14%, transparent)`,
              })}
            >
              <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6">{v.displayName}</Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}
                  >
                    <Chip size="small" label={`SKU ${v.sku}`} variant="outlined" />
                    {v.barcode && <Chip size="small" label={v.barcode} variant="outlined" />}
                    {v.attributes.map((a) => (
                      <Chip
                        key={a.definitionId}
                        size="small"
                        label={`${a.name}: ${a.value}${a.unitHint ? ` ${a.unitHint}` : ''}`}
                      />
                    ))}
                    <ActiveChip active={v.isActive} />
                  </Stack>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h5" component="div">
                    <Money value={v.salePrice} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    per {v.baseUnit.symbol}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                endIcon={<ArrowForwardRounded />}
                sx={{ mt: 2 }}
                onClick={() => {
                  close();
                  void navigate(`/products/${v.productId}`);
                }}
              >
                Open {v.productName}
              </Button>
            </Card>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
